/**
 * The overview under the chart: the whole kept history (up to 45 days) in one
 * strip, with the window the chart shows drawn on it.
 *
 * It answers the two questions the main chart cannot while zoomed in: where
 * in the history am I, and where were the bad days. The median is one plain
 * SVG line at the finest step the strip has room for (`chooseStep` over the
 * whole range, usually 6 h buckets, one or two reads), and every down span
 * is a red tick however short, because a 3-minute outage in 45 days is under
 * a pixel wide and is exactly what the reader is hunting for.
 *
 * The strip is drawn like the chart: the line over a faint fill, and the
 * part outside the window dimmed. Its length ("45 d") sits in the y axis'
 * column to its left.
 *
 * Dragging the window moves the chart; a press anywhere else centres the
 * chart there and keeps dragging from that point. The keyboard stays on the
 * main frame (arrows, Home, End), so this strip only needs a label.
 *
 * It keeps its own tile cache (`useSeriesTiles`), separate from the main
 * chart's: its tiles are a different step and are never evicted by a scroll.
 * A failed read of it tries again by itself after RETRY_MS: the strip has no
 * room for a retry button, and a paused site's page gets no new checks, which
 * is the only other thing that retries a failed tile.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type PointerEvent } from "react";
import { chartRows, chooseStep, viewSeries, type TimeRange } from "../../../../lib/monitoring";
import type { T } from "../words";
import { dayMonth, spanText } from "./labels";
import { NAVIGATOR_HEIGHT, PLOT_LEFT, PLOT_RIGHT } from "./layout";
import { useSeriesTiles } from "./useSeriesTiles";

/** The window box never gets narrower than this, or a 30-minute view on 45 days could not be grabbed. */
const MIN_BOX_PX = 8;
/** A failed read of the strip is asked again after this long. */
const RETRY_MS = 30_000;

export function ChartNavigator({
  monitorId,
  range,
  checkMinutes,
  liveKey,
  from,
  span,
  onSeek,
  t,
}: {
  monitorId: string;
  range: TimeRange;
  checkMinutes: number;
  liveKey: string | null;
  from: number;
  span: number;
  /** Put the chart's window start here (clamped by the chart). */
  onSeek: (from: number) => void;
  t: T;
}) {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const len = Math.max(1, range.end - range.start);
  const step = chooseStep(len, width, checkMinutes);
  const want = width > 0 ? { step, from: range.start, to: range.end } : null;
  const { cover, retry } = useSeriesTiles(monitorId, want, liveKey, checkMinutes);

  const drawn = useMemo(() => {
    const got = cover(step, range.start, range.end);
    const { points, downSpans } = viewSeries(got.tiles, range.start, range.end);
    // Scaled to the medians it draws. The chart's own y axis tops out at the bucket p95s,
    // and a week of 6 h buckets always holds a few slow ones, which flattened this line.
    const top = Math.max(1, ...points.map((p) => p.p50 ?? 0)) * 1.1;
    const xOf = (ms: number) => ((ms - range.start) / len) * width;
    const yOf = (ms: number) => NAVIGATOR_HEIGHT - 3 - (Math.min(ms, top) / top) * (NAVIGATOR_HEIGHT - 8);
    let line = "";
    let area = "";
    let run: string[] = [];
    let runStart = 0;
    let runEnd = 0;
    // Each unbroken run of answers is one stroke and one filled shape down to the floor.
    const close = () => {
      if (run.length === 0) return;
      line += `M${run.join("L")}`;
      area += `M${runStart.toFixed(1)},${NAVIGATOR_HEIGHT}L${run.join("L")}L${runEnd.toFixed(1)},${NAVIGATOR_HEIGHT}Z`;
      run = [];
    };
    for (const row of chartRows(points, step, checkMinutes)) {
      if (row.p50 === null) {
        close();
        continue;
      }
      const x = xOf(row.x);
      if (run.length === 0) runStart = x;
      runEnd = x;
      run.push(`${x.toFixed(1)},${yOf(row.p50).toFixed(1)}`);
    }
    close();
    const ticks = downSpans.map((s) => ({ x: xOf(s.from), w: Math.max(1.5, xOf(s.to) - xOf(s.from)) }));
    return { line, area, ticks, loading: !got.complete, failed: got.failed };
  }, [cover, step, range, len, width, checkMinutes]);

  // A retry that fails again turns `failed` off and back on, which sets the next timer.
  useEffect(() => {
    if (!drawn.failed) return;
    const id = window.setTimeout(retry, RETRY_MS);
    return () => window.clearTimeout(id);
  }, [drawn.failed, retry]);

  const boxWidth = Math.min(width, Math.max(MIN_BOX_PX, (span / len) * width));
  // A box widened to MIN_BOX_PX at the right end would otherwise stick out past now.
  const boxLeft = Math.max(0, Math.min(width - boxWidth, ((from - range.start) / len) * width));

  const drag = useRef<{ id: number; grab: number } | null>(null);
  const timeAt = (clientX: number) => {
    const rect = boxRef.current?.getBoundingClientRect();
    if (!rect || rect.width <= 0) return from;
    return range.start + ((clientX - rect.left) / rect.width) * len;
  };
  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const at = timeAt(e.clientX);
    const inside = at >= from && at <= from + span;
    const grab = inside ? at - from : span / 2;
    drag.current = { id: e.pointerId, grab };
    e.currentTarget.setPointerCapture(e.pointerId);
    e.preventDefault();
    if (!inside) onSeek(at - grab);
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (drag.current?.id !== e.pointerId) return;
    onSeek(timeAt(e.clientX) - drag.current.grab);
  };
  const onPointerEnd = (e: PointerEvent<HTMLDivElement>) => {
    if (drag.current?.id !== e.pointerId) return;
    drag.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
  };

  return (
    <div className="relative" style={{ marginLeft: PLOT_LEFT, marginRight: PLOT_RIGHT }}>
      {/* How much history the strip holds, in the y axis' column like the chart's own figures. */}
      <span
        aria-hidden
        className="crm-mut crm-tnum absolute flex items-center justify-end pr-2.5 text-[11px]"
        style={{ left: -PLOT_LEFT, width: PLOT_LEFT, top: 0, height: NAVIGATOR_HEIGHT }}
      >
        {spanText(t, len)}
      </span>
      <div
        ref={boxRef}
        role="img"
        aria-label={t("monitoring.chart.navigator")}
        className="relative cursor-pointer select-none overflow-hidden rounded-lg bg-[var(--crm-sunk)]"
        style={{ height: NAVIGATOR_HEIGHT, touchAction: "pan-y" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
      >
        {width > 0 ? (
          <svg
            aria-hidden
            width={width}
            height={NAVIGATOR_HEIGHT}
            className="absolute inset-0 transition-opacity duration-200"
            style={{ opacity: drawn.loading ? 0.45 : 1 }}
          >
            {drawn.ticks.map((tick) => (
              <rect key={tick.x} x={tick.x} y={0} width={tick.w} height={NAVIGATOR_HEIGHT} fill="var(--color-destructive)" fillOpacity={0.55} />
            ))}
            <path d={drawn.area} fill="var(--color-primary)" fillOpacity={0.16} />
            <path d={drawn.line} fill="none" stroke="var(--color-primary)" strokeOpacity={0.8} strokeWidth={1.25} strokeLinejoin="round" />
            {/* Outside the window the strip is dimmed, so the part on screen stands out. */}
            <rect x={0} y={0} width={boxLeft} height={NAVIGATOR_HEIGHT} fill="var(--crm-surface)" fillOpacity={0.55} />
            <rect
              x={boxLeft + boxWidth}
              y={0}
              width={Math.max(0, width - boxLeft - boxWidth)}
              height={NAVIGATOR_HEIGHT}
              fill="var(--crm-surface)"
              fillOpacity={0.55}
            />
          </svg>
        ) : null}
        <div
          aria-hidden
          className="absolute inset-y-0 cursor-grab rounded-md border-[1.5px] border-[var(--color-primary)] bg-[color-mix(in_oklab,var(--color-primary)_10%,transparent)] active:cursor-grabbing"
          style={{ left: boxLeft, width: boxWidth }}
        />
      </div>
      <div className="crm-mut crm-tnum mt-1 flex justify-between text-[11px]">
        <span>{dayMonth(range.start)}</span>
        <span>{t("monitoring.strip.today")}</span>
      </div>
    </div>
  );
}
